import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  ShoppingCart, 
  Dumbbell, 
  Briefcase, 
  Book, 
  Heart,
  User,
  Filter
} from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Header } from '@/components/Header';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { cn } from '@/lib/utils';

// Define task categories with their properties
const categories = [
  { id: 'all', label: 'All Tasks', icon: Filter, color: 'gray', gradient: 'from-slate-800/90 to-slate-700/90' },
  { id: 'shopping', label: 'Shopping', icon: ShoppingCart, color: 'teal', gradient: 'from-teal-500/90 to-teal-400/90' },
  { id: 'gym', label: 'Gym', icon: Dumbbell, color: 'orange', gradient: 'from-orange-400/90 to-orange-300/90' },
  { id: 'work', label: 'Work', icon: Briefcase, color: 'indigo', gradient: 'from-indigo-600/90 to-indigo-500/90' },
  { id: 'learning', label: 'Learning', icon: Book, color: 'cyan', gradient: 'from-cyan-600/90 to-cyan-500/90' },
  { id: 'health', label: 'Health', icon: Heart, color: 'mint', gradient: 'from-emerald-400/90 to-emerald-300/90' },
  { id: 'personal', label: 'Personal', icon: User, color: 'violet', gradient: 'from-violet-600/90 to-violet-500/90' },
] as const;

const Tasks: React.FC = () => {
  const navigate = useNavigate();
  const { tasks } = useApp();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const headerAction = (
    <div className="flex gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon">
            <Filter className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {categories.map((category) => (
            <DropdownMenuCheckboxItem
              key={category.id}
              checked={selectedCategory === category.id}
              onCheckedChange={() => setSelectedCategory(category.id)}
            >
              <category.icon className="w-4 h-4 mr-2" />
              {category.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Button 
        onClick={() => navigate('/create')}
        className="bg-gray-900 hover:bg-gray-800"
      >
        <Plus className="w-4 h-4 mr-2" />
        New Task
      </Button>
    </div>
  );

  // Filter tasks based on selected category
  const filteredTasks = tasks.filter(task => 
    selectedCategory === 'all' || 
    task.category.toLowerCase() === selectedCategory
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-gray-50">
      <Header 
        title="All Tasks"
        subtitle={`${filteredTasks.length} tasks total`}
        action={headerAction}
      />
      
      <div className="p-4 md:p-6 max-w-7xl mx-auto mt-24">
        {/* Category Quick Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? "default" : "outline"}
              className={cn(
                "flex items-center gap-2 font-extrabold",
                selectedCategory === category.id && "bg-slate-800 text-white"
              )}
              onClick={() => setSelectedCategory(category.id)}
            >
              <category.icon className="w-4 h-4" />
              {category.label}
            </Button>
          ))}
        </div>

        {/* Tasks List */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
          {filteredTasks.map((task) => (
            <Card 
              key={task.id} 
              className={cn(
                "w-full aspect-square backdrop-blur-md border transition-all duration-300 cursor-pointer overflow-hidden",
                "hover:scale-[1.05] group relative max-w-[150px]",
                "bg-gradient-to-br",
                task.category.toLowerCase() === 'personal' && "from-violet-600/90 to-violet-500/90 border-violet-400/20",
                task.category.toLowerCase() === 'work' && "from-indigo-600/90 to-indigo-500/90 border-indigo-400/20",
                task.category.toLowerCase() === 'health' && "from-emerald-400/90 to-emerald-300/90 border-emerald-300/20",
                task.category.toLowerCase() === 'learning' && "from-cyan-600/90 to-cyan-500/90 border-cyan-400/20",
                task.category.toLowerCase() === 'shopping' && "from-teal-500/90 to-teal-400/90 border-teal-400/20",
                task.category.toLowerCase() === 'gym' && "from-orange-400/90 to-orange-300/90 border-orange-300/20"
              )}
              onClick={() => navigate(`/task/${task.id}`)}
            >
              <CardContent className="p-2.5 h-full flex flex-col relative">
                <div className="flex-1 min-h-0">
                  <div className="flex flex-col items-start gap-1">
                    <h3 className="font-extrabold text-white truncate text-sm w-full">
                      {task.title}
                    </h3>
                    {task.isRecurring && (
                      <Badge variant="outline" className="text-[10px] px-1 py-0 border-white/40 bg-white/10 text-white backdrop-blur-sm font-extrabold">
                        ↻
                      </Badge>
                    )}
                  </div>
                  {task.description && (
                    <p className="text-[10px] text-white/90 line-clamp-2 font-bold mt-1">
                      {task.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 mt-auto">
                  <Badge 
                    className={cn(
                      "capitalize backdrop-blur-sm font-extrabold border-white/20 bg-white/20 text-white text-[10px] px-1 py-0"
                    )}
                  >
                    {task.category}
                  </Badge>
                  <Badge 
                    variant="outline" 
                    className="border-white/40 bg-white/10 text-white backdrop-blur-sm font-extrabold text-[10px] px-1 py-0"
                  >
                    {new Date(task.dueDate!).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}

          {filteredTasks.length === 0 && (
            <div className="col-span-full">
              <div className="text-center py-12">
                <div className="w-12 h-12 rounded-xl bg-slate-800/90 backdrop-blur-md border border-white/20 flex items-center justify-center mx-auto mb-4">
                  <Filter className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-slate-900 font-extrabold text-lg">No tasks found</h3>
                <p className="text-slate-600 mt-2 font-bold text-sm">
                  {selectedCategory === 'all' 
                    ? "Create a new task to get started"
                    : `No tasks in the ${categories.find(c => c.id === selectedCategory)?.label} category`
                  }
                </p>
                <Button
                  variant="default"
                  size="sm"
                  className="mt-4 bg-slate-800 hover:bg-slate-700 text-white font-extrabold"
                  onClick={() => navigate('/create')}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Create Task
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Tasks; 